import { useEffect, useMemo, useState } from 'react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useChannel } from '@/hooks/useChannel';
import { useCheckPermission } from '@/hooks/useCheckPermission';
import { cn } from '@/lib/utils';
import { defaultMessageConfig } from '@utils/emailConfigurations';
import { formatOrderPrice } from './order-totals';

const ORDER_TYPE_LABEL = {
	reservation: 'Reservation',
	'click-collect': 'Click & Collect'
};

const PAID_ORDER_TYPES = new Set(['click-collect']);

/**
 * Email-customisation modal that gates the `confirmed` and `cancelled`
 * transitions. There's no Lovable counterpart — this is built from the local
 * Unify primitives (Dialog / Textarea / native checkbox / Button-shaped
 * pill CTAs) so it reads as a sibling of `OrderDetailModal`.
 *
 * Behaviour mirrors `dashboard-shared/pages/orders/components/OrdersUpdateModal`
 * exactly on the wire:
 *   • Prefill message from `channel.config.templates[type].message`,
 *     else `defaultMessageConfig[type](shop).message`.
 *   • Send the full mutated order with `{ ...order, emailMessage, skipEmail }`
 *     via the parent's `onSubmit` callback.
 *   • Disable the primary CTA when the message is empty and we're sending.
 */
export function OrderUpdateModal({ pendingUpdate, shop, onClose, onSubmit }) {
	const canManage = useCheckPermission('org:orders:manage');
	const channelType = pendingUpdate?.orderType === 'reservation' ? 'product-reservations' : 'local-checkout';
	const { channel } = useChannel(channelType, shop?.id);

	const [sendEmail, setSendEmail] = useState(true);
	const [emailMessage, setEmailMessage] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const type = pendingUpdate?.status === 'confirmed' ? 'confirmation' : 'cancellation';

	useEffect(() => {
		if (!pendingUpdate || !shop) return;
		const template = channel?.config?.templates?.[type]?.message;
		const fallback = defaultMessageConfig[type]?.(shop)?.message || '';
		setEmailMessage(template || fallback);
		setSendEmail(true);
	}, [pendingUpdate, shop, channel, type]);

	const isPaid = pendingUpdate?.status === 'cancelled' && pendingUpdate?.history?.some?.((h) => h.status === 'paid');
	const refundAware = useMemo(() => {
		if (!isPaid || !pendingUpdate) return null;
		return `Cancelling will refund the customer ${formatOrderPrice(pendingUpdate)}.`;
	}, [isPaid, pendingUpdate]);

	const emailIsEmpty = !emailMessage.trim().length;
	const submitDisabled = !canManage || submitting || (sendEmail && emailIsEmpty);

	const confirming = pendingUpdate?.status === 'confirmed';
	const verb = confirming ? 'Confirm' : 'Cancel';
	const verbing = confirming ? 'Confirming' : 'Cancelling';
	const orderRef = pendingUpdate?.pickupReference || pendingUpdate?.id || '';
	const summary = pendingUpdate
		? [pendingUpdate.customerName, formatOrderPrice(pendingUpdate), ORDER_TYPE_LABEL[pendingUpdate.orderType] || 'Order']
				.filter(Boolean)
				.join(' · ')
		: '';

	const submit = async () => {
		if (submitDisabled || !pendingUpdate) return;
		setSubmitting(true);
		try {
			await onSubmit?.({ ...pendingUpdate, emailMessage, skipEmail: !sendEmail });
			// Defer the close so Radix's RemoveScroll utility unwinds body pointer-events
			// cleanly. Closing synchronously inside the same tick as the submit click can
			// leave `body { pointer-events: none }` stuck, freezing the whole page.
			// Only fire on success — if onSubmit throws, keep the modal open so the
			// user can see the error toast and retry rather than silently dismissing.
			setTimeout(() => onClose?.(), 0);
		} catch {
			// The parent re-throws after toasting so we know to keep the modal
			// open. Swallow the rejection here so it doesn't bubble up to the
			// React click handler as an unhandled promise rejection.
		} finally {
			setSubmitting(false);
		}
	};

	// Belt-and-braces: Radix Dialog caches body.style.pointerEvents on open and
	// restores it on close. When the modal was opened from another Radix layer
	// (DropdownMenu / nested Dialog), the cached value is the *locked* `'none'`
	// — so closing the modal restores the page to `pointer-events: none` and
	// freezes the whole UI. After each close, wait a beat for Radix to finish
	// its own cleanup, then force the body back to interactive if it's stuck.
	useEffect(() => {
		if (pendingUpdate) return undefined;
		const id = setTimeout(() => {
			if (typeof document !== 'undefined' && document.body.style.pointerEvents === 'none') {
				document.body.style.pointerEvents = '';
			}
		}, 300);
		return () => clearTimeout(id);
	}, [pendingUpdate]);

	const primaryLabel = sendEmail ? `${verb} order & send email` : `${verb} order`;

	return (
		<Dialog open={!!pendingUpdate} onOpenChange={(v) => !v && onClose?.()}>
			<DialogContent className="rounded-2xl sm:max-w-lg">
				<div className="text-sm text-muted-foreground">Unify by NearSt · {shop?.name}</div>
				<DialogTitle className="mt-1 text-2xl font-extrabold tracking-tight">
					{verbing} order #{orderRef}
				</DialogTitle>
				{summary ? <DialogDescription className="text-sm text-muted-foreground">{summary}</DialogDescription> : null}
				{refundAware ? <p className="text-sm font-semibold text-action">{refundAware}</p> : null}

				<label className="flex cursor-pointer items-center gap-2 pt-2 text-sm font-medium text-foreground">
					<input
						type="checkbox"
						checked={sendEmail}
						onChange={() => setSendEmail((v) => !v)}
						className="h-4 w-4 cursor-pointer rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					/>
					Send {confirming ? 'confirmation' : 'cancellation'} email to customer
				</label>

				{sendEmail ? (
					<div className="space-y-2">
						<div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
							Email preview · customise the message below
						</div>
						<textarea
							data-testid="orders-update-email-message"
							value={emailMessage}
							onChange={(e) => setEmailMessage(e.target.value)}
							className={cn(
								'flex min-h-[200px] w-full rounded-md border border-input bg-background p-3 text-sm leading-relaxed',
								'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
							)}
						/>
						{emailIsEmpty ? (
							<p className="text-sm text-destructive">Add a message so the customer knows what's happening.</p>
						) : null}
					</div>
				) : (
					<div className="rounded-xl bg-muted/50 p-3 text-sm text-muted-foreground">
						No email will be sent to the customer about this order.
					</div>
				)}

				<div className="flex justify-end gap-2 pt-3">
					<button
						type="button"
						onClick={() => onClose?.()}
						className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary"
					>
						Cancel
					</button>
					<button
						type="button"
						data-testid="confirm-order"
						onClick={submit}
						disabled={submitDisabled}
						className="rounded-full bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
						title={canManage ? undefined : "You don't have permission to manage orders"}
					>
						{primaryLabel}
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
